import { ProjectCollaboratorDto, ProjectDto } from '@/api/ApiClient';
import { isFacultyRole, isOtherCollaboratorRole, isPIRole, isStaffRole, ROLE_IDS } from '@/lib/utils/roleUtils';

/**
 * UI-oriented project model used in tree state for both readonly and edit flows.
 * It keeps collaborator roles split by responsibility while retaining legacy
 * fields used by existing components.
 */
export interface ProjectUIModel extends Omit<ProjectDto, 'user' | 'faculty' | 'staff' | 'collaborators'> {
  piUserId?: string;
  facultyUserIds: string[];
  staffUserIds: string[];
  otherCollaborators: ProjectCollaboratorDto[];
  // Legacy compatibility fields (derived from role slices)
  user?: string | undefined;
  faculty?: string[] | undefined;
  staff?: string[] | undefined;
  collaborators?: ProjectCollaboratorDto[] | undefined;
}

const dedupeIds = (ids: (string | undefined | null)[]) =>
  Array.from(new Set(ids.map((id) => id?.trim()).filter((id): id is string => !!id)));

const dedupeCollaborators = (collaborators: ProjectCollaboratorDto[]) => {
  const seen = new Set<string>();
  return collaborators.filter((c) => {
    const id = c.id?.trim();
    const roleId = c.roleId;
    if (!id || !roleId) return false;
    const key = `${id}::${roleId}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export function buildProjectCollaborators(project: ProjectUIModel): ProjectCollaboratorDto[] {
  const piCollaborators = project.piUserId
    ? [{ id: project.piUserId, roleId: ROLE_IDS.ProjectUserPI } as ProjectCollaboratorDto]
    : [];
  const facultyCollaborators = (project.facultyUserIds ?? []).map(
    (id) => ({ id, roleId: ROLE_IDS.ProjectFaculty }) as ProjectCollaboratorDto
  );
  const staffCollaborators = (project.staffUserIds ?? []).map(
    (id) => ({ id, roleId: ROLE_IDS.ProjectStaff }) as ProjectCollaboratorDto
  );
  const otherCollaborators = (project.otherCollaborators ?? []).filter((c) => isOtherCollaboratorRole(c.roleId));

  return dedupeCollaborators([
    ...otherCollaborators,
    ...facultyCollaborators,
    ...staffCollaborators,
    ...piCollaborators,
  ]);
}

export function normalizeProjectUIModel(project: ProjectUIModel): ProjectUIModel {
  const piUserId = project.piUserId?.trim() || undefined;
  const facultyUserIds = dedupeIds(project.facultyUserIds ?? []);
  const staffUserIds = dedupeIds(project.staffUserIds ?? []);
  const otherCollaborators = dedupeCollaborators(
    (project.otherCollaborators ?? []).filter((c) => isOtherCollaboratorRole(c.roleId))
  );

  const normalized: ProjectUIModel = {
    ...project,
    piUserId,
    facultyUserIds,
    staffUserIds,
    otherCollaborators,
  };

  const collaborators = buildProjectCollaborators(normalized);
  return {
    ...normalized,
    user: piUserId,
    faculty: facultyUserIds,
    staff: staffUserIds,
    collaborators,
  };
}

export function toProjectUIModel(project: ProjectDto): ProjectUIModel {
  const collaborators = project.collaborators ?? [];
  const piFromCollaborators = collaborators.find((c) => isPIRole(c.roleId))?.id;
  const facultyFromCollaborators = collaborators.filter((c) => isFacultyRole(c.roleId)).map((c) => c.id);
  const staffFromCollaborators = collaborators.filter((c) => isStaffRole(c.roleId)).map((c) => c.id);

  const model: ProjectUIModel = {
    ...project,
    piUserId: project.user ?? piFromCollaborators,
    facultyUserIds: dedupeIds([...(project.faculty ?? []), ...facultyFromCollaborators]),
    staffUserIds: dedupeIds([...(project.staff ?? []), ...staffFromCollaborators]),
    otherCollaborators: collaborators.filter((c) => isOtherCollaboratorRole(c.roleId)),
  };

  return normalizeProjectUIModel(model);
}

/** All user ids assigned to the project (PI, faculty, staff, and additional collaborators). */
export function getProjectMemberUserIds(project: ProjectUIModel): Set<string> {
  return new Set<string>([
    ...(project.piUserId ? [project.piUserId] : []),
    ...(project.facultyUserIds ?? []),
    ...(project.staffUserIds ?? []),
    ...(project.otherCollaborators ?? []).map((c) => c.id).filter((id): id is string => !!id),
  ]);
}

export function isUserOnProject(project: ProjectUIModel, userId: string | undefined): boolean {
  if (!userId) return false;
  return getProjectMemberUserIds(project).has(userId);
}

/** Additional Collaborators tab: project editors and anyone assigned to the project. */
export function canShowProjectCollaboratorsTab(
  project: ProjectUIModel | undefined | null,
  opts: { canEditProject: boolean; currentUserId: string | undefined }
): boolean {
  return !!project && (opts.canEditProject || isUserOnProject(project, opts.currentUserId));
}
